import { useState } from "react";
import { Button } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import {
  useGetReservations,
  useGetReservation,
} from "../../utils/react-query/queryReservations/queries";
import {
  useCreateReservation,
  useEditReservation,
  useApproveReservation,
  useRejectReservation,
} from "../../utils/react-query/queryReservations/queries";
import { useMyPermissions } from "../../utils/react-query/queryRbac/queries";
import { successToast, errorToast } from "../../services/toasts";
import { ReservationList } from "./ReservationList";
import { ReservationModal } from "./ReservationModal";
import { ReservationDetail } from "./ReservationDetail";
import type {
  ReservationDetail as ReservationDetailType,
  CreateReservationPayload,
  EditReservationPayload,
} from "./types";
import styles from "./ReservationsPage.module.scss";

export function ReservationsPage() {
  // Permissions
  const {
    data: permissions = new Set<string>(),
    isLoading: permissionsLoading,
  } = useMyPermissions();

  const canViewAll = permissions.has("reservations:view_all");
  const canViewOwn = permissions.has("reservations:view_own");
  const canCreate = permissions.has("reservations:create");
  const canApprove = permissions.has("reservations:approve");
  const canEditOwn = permissions.has("reservations:edit_own");

  // Data
  const { data: reservationsData, isLoading } = useGetReservations();

  // Mutations
  const { mutateAsync: createReservation } = useCreateReservation();
  const { mutateAsync: editReservation } = useEditReservation();
  const { mutateAsync: approveReservation } = useApproveReservation();
  const { mutateAsync: rejectReservation } = useRejectReservation();

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] =
    useState<ReservationDetailType | null>(null);
  const [viewingReservationId, setViewingReservationId] = useState<number | null>(null);

  // Fetch reservation detail when viewing
  const { data: viewingReservation, isLoading: isLoadingDetail } =
    useGetReservation(viewingReservationId ?? 0);

  // Permission gate
  if (!permissionsLoading && !canViewAll && !canViewOwn) {
    return (
      <div className={styles.noPermissions}>
        <p>No tienes permisos para ver reservas.</p>
      </div>
    );
  }

  const handleCreate = async (payload: CreateReservationPayload) => {
    try {
      await createReservation(payload);
      setIsCreateModalOpen(false);
      successToast("Reserva creada exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al crear la reserva";
      errorToast(message);
      // Re-throw so the modal can inspect for field-level errors
      throw err;
    }
  };

  const handleEdit = async (id: number, payload: EditReservationPayload) => {
    try {
      await editReservation({ id, payload });
      setEditingReservation(null);
      successToast("Reserva actualizada exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al editar la reserva";
      errorToast(message);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await approveReservation(id);
      setViewingReservationId(null);
      successToast("Reserva aprobada exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al aprobar la reserva";
      errorToast(message);
    }
  };

  const handleReject = async (id: number, reason?: string) => {
    try {
      await rejectReservation({ id, reason });
      setViewingReservationId(null);
      successToast("Reserva rechazada");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al rechazar la reserva";
      errorToast(message);
    }
  };

  // canEditOwn is used to show/hide the edit button
  // The backend verifies if the user actually created the reservation

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.heading}>Reservas</h2>
        {canCreate && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Nueva Reserva
          </Button>
        )}
      </div>

      <ReservationList
        data={reservationsData?.results ?? []}
        isLoading={isLoading}
        canApprove={canApprove}
        canEditOwn={canEditOwn}
        onView={(row) => setViewingReservationId(row.id)}
        onEdit={(row) => setEditingReservation(row as unknown as ReservationDetailType)}
        onApprove={(row) => handleApprove(row.id)}
        onReject={(row) => handleReject(row.id)}
      />

      {/* Create Modal */}
      {isCreateModalOpen && (
        <ReservationModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreate}
          onEdit={handleEdit}
        />
      )}

      {/* Edit Modal */}
      {editingReservation && (
        <ReservationModal
          open={!!editingReservation}
          reservation={editingReservation}
          onClose={() => setEditingReservation(null)}
          onCreate={handleCreate}
          onEdit={handleEdit}
        />
      )}

      {/* Detail Dialog */}
      {viewingReservationId && (
        <ReservationDetail
          open={!!viewingReservationId}
          reservation={viewingReservation ?? null}
          isLoading={isLoadingDetail}
          canApprove={canApprove}
          onClose={() => setViewingReservationId(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
    </div>
  );
}

export default ReservationsPage;
